<template>
	<section ref="mastersSectionRef" class="pc-masters">
		<div class="pc-center-1360 masters-content">
			<YyevaAnimation
				ref="mastersAnimationRef"
				:src="mastersVideoUrl"
				:width="584"
				:height="378"
				:loop="false"
				:end-pause="true"
				class="masters-animation"
				@end="handleMastersAnimationEnd"
			/>
			<div class="guide" :class="{ 'guide--en': locale === 'en' }" role="note">
				<span class="guide__badge" aria-hidden="true">AI</span>
				<div class="guide__text">
					<p class="guide__line1">{{ t("index.masters.guide.line1") }}</p>
					<p class="guide__line2">{{ t("index.masters.guide.line2") }}</p>
				</div>
			</div>

			<div class="send-box">
				<button type="button" class="expand-btn" @click="handleSend">
					<img src="../../../images/pc/master-expand.svg" class="expand-icon" alt="" />
				</button>

				<p class="greeting">{{ t("index.masters.greeting") }}</p>

				<div class="tags">
					<button
						v-for="(item, index) in tagList"
						:key="item.key"
						type="button"
						class="tag"
						:class="{ active: selectedToolType === item.key }"
						@click="handleTagClick(item, index)"
					>
						<img :src="item.icon" class="tag-icon" alt="" />

						<span>{{ item.label }}</span>
					</button>
				</div>

				<div v-if="selectedToolType === 'bazi' && isLoggedInClient" class="panel-area">
					<PcBaziFilePanel :user-mess="userMess" @send="handlePanelSend" />
				</div>
				<div v-else-if="selectedToolType === 'tarot'" class="panel-area">
					<QuestionList :type="2" @send="handlePanelSend" />
				</div>
				<div v-else-if="selectedToolType === 'liuyao'" class="panel-area">
					<QuestionList :type="4" @send="handlePanelSend" />
				</div>

				<div class="input-box">
					<input
						v-model="question"
						type="text"
						class="input"
						:placeholder="placeholder"
						@focus="handleInputFocus"
						@keyup.enter="handleSend"
					/>

					<button type="button" class="send-btn" @click="handleSend">
						<img src="../../../images/pc/master-send.png" class="send-icon" alt="" />
					</button>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, inject } from "vue";

import { useI18n } from "~/composables/useI18n";
import { useLangImg } from "~/composables/useLangImg";
import {
	useHomepageTracking,
	useSectionView,
} from "@variants/index/composables/useHomepageTracking";

import bazi from "../../../images/pc/bazi.png";

import tarot from "../../../images/pc/tarot.png";

import liuyao from "../../../images/pc/liuyao.png";

import chat from "../../../images/pc/chat.png";

import YyevaAnimation from "~/components/yyeva-animation.vue";
import mastersVideoUrl from "./masters.mp4?url";
import PcBaziFilePanel from "@variants/seer/master-s/pc/components/PcBaziFilePanel.vue";
import QuestionList from "@variants/seer/master-s/pc/components/QuestionList.vue";
import { showAddBaziFileToast } from "@variants/seer/master-s/shared/utils/show-add-bazi-toast";

const { t, locale } = useI18n();
const { track } = useHomepageTracking("pc");
const { isLogin } = storeToRefs(useUserStore());
const openLoginPopup = inject("openLoginPopup", null);

const question = ref("");
const selectedToolType = ref("chat");
const mastersSectionRef = ref(null);
const mastersAnimationRef = ref(null);
let mastersReplayTimer = null;
const MASTERS_ANIMATION_REPLAY_DELAY = 15000;

const isLoggedInClient = computed(
	() => Boolean(isLogin.value) || (import.meta.client && Boolean(localStorage.getItem("token")))
);

/** 未登录不初始化 store，避免触发档案列表请求 */
const getBaziFileStore = () => {
	if (!isLoggedInClient.value) return null;
	return useBaziFileStore();
};

const userMess = computed(() => {
	const store = getBaziFileStore();
	const file = store?.baziFile;
	if (!file?.user_name) return null;
	return {
		id: Number(file.id) || 0,
		sex: Number(file.gender) || 0,
		user_name: file.user_name,
		bir_timestamp: String(file.bir_timestamp || ""),
	};
});

const hasBaziFile = computed(() => Boolean(userMess.value?.user_name));

const ensureBaziProfile = async () => {
	const store = getBaziFileStore();
	if (!store) return;
	// 每次进入八字面板都拉最新档案（刷新后 isGetFileList 不再持久化）
	await store.getbaziFileList?.().catch(() => {});
};

watch(
	[selectedToolType, isLoggedInClient],
	([key, loggedIn]) => {
		if (key === "bazi" && loggedIn) ensureBaziProfile();
	},
	{ immediate: true }
);

const clearMastersReplayTimer = () => {
	if (mastersReplayTimer) {
		clearTimeout(mastersReplayTimer);
		mastersReplayTimer = null;
	}
};

const handleMastersAnimationEnd = () => {
	clearMastersReplayTimer();
	mastersReplayTimer = setTimeout(() => {
		mastersAnimationRef.value?.start?.();
	}, MASTERS_ANIMATION_REPLAY_DELAY);
};

onBeforeUnmount(() => {
	clearMastersReplayTimer();
});
const placeholder = computed(() => t("index.masters.placeholder"));
const getLangImg = useLangImg(
	import.meta.glob("../../../images/pc/*/*.{png,jpg,jpeg,webp,svg,gif}", {
		eager: true,

		import: "default",
	})
);

const tagList = computed(() => [
	{ key: "bazi", modelType: 1, icon: bazi, label: t("index.masters.tags.bazi") },

	{ key: "tarot", modelType: 2, icon: tarot, label: t("index.masters.tags.tarot") },

	{ key: "liuyao", modelType: 3, icon: liuyao, label: t("index.masters.tags.liuyao") },

	{ key: "chat", modelType: 4, icon: chat, label: t("index.masters.tags.chat") },
]);

useSectionView(mastersSectionRef, {
	track,
	sectionId: "masters_chat",
});

const isClientLoggedIn = () =>
	import.meta.client && Boolean(localStorage.getItem("token") || isLogin.value);

/** 登录成功后裸进 MasterS，不带 question / model */
const goToMasterSBare = () => {
	navigateTo({ path: "/seer/master-s" });
};

const requireLogin = source => {
	openLoginPopup?.(goToMasterSBare, null, true, source);
};

const handleTagClick = (item, index) => {
	selectedToolType.value = item.key;
	track("masters_tool_tab_click", {
		tool_type: item.key,
		element_text: item.label,
		position: index + 1,
	});
	if (!isClientLoggedIn()) {
		requireLogin("masters_home_tag");
	}
};

const handleInputFocus = event => {
	track("masters_input_focus", {
		...(selectedToolType.value ? { selected_tool_type: selectedToolType.value } : {}),
	});
};

const resolveHomeModelType = () => {
	const selected = tagList.value.find(item => item.key === selectedToolType.value);
	return selected?.modelType || 4;
};

/** 已登录：带当前 model，有内容则带 question */
const goToMasterS = () => {
	const trimmed = question.value.trim();
	navigateTo({
		path: "/seer/master-s",
		query: {
			model: String(resolveHomeModelType()),
			...(trimmed ? { question: trimmed } : {}),
		},
	});
};

const handleSend = async () => {
	const trimmed = question.value.trim();

	if (!isClientLoggedIn()) {
		track("masters_question_submit", {
			...(selectedToolType.value ? { selected_tool_type: selectedToolType.value } : {}),
			question_length: trimmed.length,
			result: "fail",
		});
		requireLogin("masters_home_send");
		return;
	}

	// 八字无档案不能发送问题（空发送仍可进 MasterS）
	if (selectedToolType.value === "bazi" && trimmed) {
		await ensureBaziProfile();
		if (!hasBaziFile.value) {
			track("masters_question_submit", {
				selected_tool_type: "bazi",
				question_length: trimmed.length,
				result: "fail",
			});
			showAddBaziFileToast();
			return;
		}
	}

	track("masters_question_submit", {
		...(selectedToolType.value ? { selected_tool_type: selectedToolType.value } : {}),
		question_length: trimmed.length,
		result: "success",
	});
	goToMasterS();
};

/** 点击问题只填入输入框，不跳转；发送由发送按钮触发 */
const handlePanelSend = text => {
	question.value = String(text || "").trim();
};
</script>

<style lang="scss" scoped>
.pc-masters {
	width: auto;

	height: auto;
	min-height: 709px;

	background: url("../../../images/pc/mask.png") no-repeat center top/ 100% auto;

	margin: 100px auto 0;

	.masters-content {
		position: relative;

		height: 100%;

		.masters-animation {
			position: absolute;
			left: 50%;
			top: -80px;
			transform: translateX(-50%);
			pointer-events: none;
		}

		.guide {
			position: absolute;

			top: 180px;

			right: 200px;

			display: flex;

			align-items: center;

			justify-content: center;

			gap: 6px;

			box-sizing: border-box;

			width: 203px;

			height: 64px;

			padding: 0 10px;

			background: url("../../../images/pc/bubble.png") no-repeat center / 100% 100%;

			animation: masters-guide-bounce 1.6s ease-out both;

			&__badge {
				flex-shrink: 0;

				display: flex;

				align-items: center;

				justify-content: center;

				width: 40px;

				height: 40px;

				border-radius: 9px;

				background: linear-gradient(180deg, #ffb84d 0%, #f5a623 100%);

				font-size: 16px;

				line-height: 1;

				font-weight: bold;

				color: #ffffff;
			}

			&__text {
				flex-shrink: 0;

				display: flex;

				flex-direction: column;

				justify-content: center;

				gap: 1px;
			}

			&__line1,
			&__line2 {
				margin: 0;

				overflow: hidden;

				text-overflow: ellipsis;

				white-space: nowrap;
			}

			&__line1 {
				font-size: 16px;

				line-height: 18px;

				color: #333333;
			}

			&__line2 {
				font-size: 16px;

				line-height: 16px;
				color: #333333;
			}

			&--en {
				.guide__line1 {
					font-size: 14px;

					line-height: 15px;
				}

				.guide__line2 {
					font-size: 14px;

					line-height: 14px;
				}
			}
		}

		.send-box {
			position: absolute;

			left: 50%;

			top: 300px;

			transform: translateX(-50%);

			width: 100%;
			max-width: 1208px;
			box-sizing: border-box;

			padding: 29px 42px 42px;

			background: #ffffff;

			border-radius: 30px;

			box-shadow: 0 10px 13.9px rgba(181, 125, 0, 0.16);
			border: 1px solid #ffe19d;

			.expand-btn {
				position: absolute;
				top: 29px;
				right: 42px;
				z-index: 1;
				display: flex;
				align-items: center;
				justify-content: center;
				width: 30px;
				height: 30px;
				padding: 0;
				border: none;
				background: transparent;
				cursor: pointer;

				&:hover {
					opacity: 0.85;
				}

				.expand-icon {
					display: block;
					width: 30px;
					height: 30px;
				}
			}

			.greeting {
				display: inline-block;

				margin: 0 0 78px;

				padding: 12px 24px;

				background: #f5f5f5;

				border-radius: 0 60px 60px 34px;

				font-size: 20px;

				line-height: 28px;

				color: #333333;
			}

			.tags {
				display: flex;

				flex-wrap: wrap;

				gap: 24px;

				margin: 0 0 24px;

				.tag {
					display: inline-flex;

					align-items: center;

					justify-content: center;

					gap: 4px;

					padding: 12px 24px;

					border: 0.815px solid #fff4dd;

					border-radius: 61px;

					background: linear-gradient(
						to bottom,
						rgba(255, 237, 172, 0.7) 0%,
						rgba(255, 254, 252, 0.7) 89.583%
					);

					cursor: pointer;

					font-size: 20px;

					line-height: 18px;

					color: #333333;

					&.active {
						background: #333333;
						border-color: #333333;
						color: #ffffff;

						.tag-icon {
							filter: brightness(0) invert(1);
						}
					}

					.tag-icon {
						width: 16px;

						height: 16px;

						flex-shrink: 0;
					}
				}
			}

			.panel-area {
				margin: 0 0 24px;

				:deep(.question-panel) {
					margin: 0;
					display: flex;
					flex-direction: column;
					gap: 8px;
					padding: 8px 16px;
					border: 1px solid #f7f7f7;
					border-radius: 10px;
					background: linear-gradient(180deg, #fff8e9 0%, #fff 100%);
					overflow: hidden;
				}

				:deep(.question-title) {
					margin: 0;
					padding: 0;
					font-size: 18px;
					font-weight: 700;
					line-height: normal;
					color: #333;
				}

				:deep(.question-list) {
					gap: 8px;
					padding: 0;
					overflow: hidden;
				}

				:deep(.question-list::-webkit-scrollbar) {
					display: none;
				}

				:deep(.question-chip) {
					height: auto;
					min-height: unset;
					padding: 8px;
					border: 0.815px solid #fff4dd;
					border-radius: 61px;
					background: rgba(255, 255, 255, 0.7);
					color: #333;
					font-size: 16px;
					font-weight: 700;
					line-height: 16px;
					cursor: pointer;
				}
			}

			.input-box {
				display: flex;

				align-items: center;

				height: 80px;

				min-height: 80px;

				padding: 19px 19px 19px 32px;

				background: #f6f6f6;

				border-radius: 75px;

				box-sizing: border-box;

				.input {
					flex: 1;

					min-width: 0;

					border: none;

					background: transparent;

					outline: none;

					font-size: 20px;

					line-height: 28px;

					color: #333333;

					&::placeholder {
						color: #999999;
					}
				}

				.send-btn {
					flex-shrink: 0;

					display: flex;

					align-items: center;

					justify-content: center;

					border: none;

					padding: 0;

					background: transparent;

					cursor: pointer;

					&:hover {
						opacity: 0.85;
					}

					.send-icon {
						width: 42px;

						height: 42px;
					}
				}
			}
		}
	}
}

@keyframes masters-guide-bounce {
	0%,
	100% {
		transform: translateY(0);
	}

	20% {
		transform: translateY(-14px);
	}

	40% {
		transform: translateY(0);
	}

	55% {
		transform: translateY(-8px);
	}

	70% {
		transform: translateY(0);
	}

	82% {
		transform: translateY(-4px);
	}
}
</style>
